<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
    <!-- Animated Background -->
    <div class="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent pointer-events-none"></div>
    <div class="fixed inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
    
    <!-- Floating Particles -->
    <div class="fixed inset-0 pointer-events-none">
      <div class="particle particle-1"></div>
      <div class="particle particle-2"></div>
      <div class="particle particle-3"></div>
      <div class="particle particle-4"></div>
      <div class="particle particle-5"></div>
    </div>

    <!-- Sidebar -->
    <div class="fixed inset-y-0 left-0 w-72 bg-slate-800/90 backdrop-blur-xl border-r border-slate-700/50 z-30">
      <nav class="h-full flex flex-col">
        <!-- Logo Section -->
        <div class="p-6 border-b border-slate-700/50">
          <router-link to="/dashboard" class="flex items-center space-x-3 group">
            <div class="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <svg class="w-6 h-6 text-slate-900" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div>
              <h1 class="text-xl font-bold text-white">OilBuddy AI</h1>
              <p class="text-xs text-slate-400">Contractor Portal</p>
            </div>
          </router-link>
        </div>

        <!-- Navigation -->
        <div class="flex-1 overflow-y-auto py-6">
          <div class="mb-8">
            <div class="flex items-center px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              <span>Main</span>
            </div>
            <div class="space-y-1">
              <router-link to="/dashboard" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
                </svg>
                <span>Dashboard</span>
              </router-link>
            </div>
          </div>

          <div class="mb-8">
            <div class="flex items-center px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              <span>Work</span>
            </div>
            <div class="space-y-1">
              <router-link to="/jobs" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd"/>
                  <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"/>
                </svg>
                <span>Job Marketplace</span>
              </router-link>

              <router-link to="/timesheets" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>
                <span>Timesheets</span>
              </router-link>

              <router-link to="/payments" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"/>
                </svg>
                <span>Payments</span>
              </router-link>
            </div>
          </div>

          <div class="mb-8">
            <div class="flex items-center px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              <span>Management</span>
            </div>
            <div class="space-y-1">
              <router-link to="/scheduling" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                </svg>
                <span>Scheduling</span>
              </router-link>

              <router-link to="/safety" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"/>
                </svg>
                <span>Safety & Compliance</span>
              </router-link>

              <router-link to="/profile" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                </svg>
                <span>Profile & Certifications</span>
              </router-link>
            </div>
          </div>

          <div class="mb-8">
            <div class="flex items-center px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              <span>Tools</span>
            </div>
            <div class="space-y-1">
              <router-link to="/ai-messaging" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"/>
                </svg>
                <span>AI Assistant</span>
              </router-link>

              <router-link to="/knowledge" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
                </svg>
                <span>Knowledge Hub</span>
              </router-link>

              <router-link to="/blog" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
                </svg>
                <span>Industry News</span>
              </router-link>

              <router-link to="/store" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-.89l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7 1a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
                </svg>
                <span>Store</span>
              </router-link>
            </div>
          </div>

          <div class="mb-8">
            <div class="flex items-center px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              <span>Account</span>
            </div>
            <div class="space-y-1">
              <router-link to="/settings" class="nav-item group">
                <svg class="w-5 h-5 mr-3 group-hover:text-emerald-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.286c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.286c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.286-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd"/>
                </svg>
                <span>Settings</span>
              </router-link>
            </div>
          </div>
        </div>

        <!-- User Profile Section -->
        <div class="p-4 border-t border-slate-700/50">
          <div class="flex items-center space-x-3 p-3 bg-slate-700/30 rounded-xl hover:bg-slate-600/50 transition-all duration-200 cursor-pointer group mb-3" @click="handleProfileClick">
            <div class="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <span class="text-sm font-bold text-white">
                {{ userInitials || 'U' }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-white group-hover:text-emerald-400 transition-colors truncate">{{ userName || 'User' }}</p>
              <p class="text-xs text-slate-400">{{ userTitle || 'Contractor' }}</p>
            </div>
            <svg class="w-4 h-4 text-slate-400 group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4"/>
            </svg>
          </div>
          
          <!-- Logout Button -->
          <button @click="logout" class="w-full flex items-center justify-center space-x-2 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-200">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
            <span class="text-sm font-medium">Sign Out</span>
          </button>
        </div>
      </nav>
    </div>

    <!-- Main Content Area -->
    <div class="ml-72 min-h-screen">
      <!-- Top Navigation Bar -->
      <div class="fixed top-0 left-72 right-0 z-40 bg-slate-800/90 backdrop-blur-xl border-b border-slate-700/50">
        <div class="flex items-center justify-between px-6 py-4">
          <!-- Left side - Breadcrumb -->
          <div class="flex items-center space-x-4">
            <router-link to="/store" class="text-slate-400 hover:text-emerald-400 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </router-link>
            <span class="text-slate-400">/</span>
            <span class="text-white font-medium">{{ product?.name || 'Product' }}</span>
          </div>
          
          <!-- Right side - Cart -->
          <div class="flex items-center space-x-4">
            <button @click="showCart = true" class="relative p-2 text-slate-400 hover:text-emerald-400 transition-colors rounded-lg">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
              </svg>
              <div v-if="cartItemCount > 0" class="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                {{ cartItemCount }}
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Page Content -->
      <div class="pt-28 p-8">
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>

        <div v-else-if="!product" class="text-center py-20">
          <svg class="w-16 h-16 text-slate-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33"/>
          </svg>
          <h2 class="text-xl font-semibold text-white mb-2">Product Not Found</h2>
          <p class="text-slate-400 mb-6">The product you're looking for doesn't exist.</p>
          <router-link to="/store" class="inline-flex items-center px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors">
            Back to Store
          </router-link>
        </div>

        <div v-else class="max-w-7xl mx-auto">
          <!-- Product Details -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <!-- Product Images -->
            <div class="space-y-6">
              <div class="aspect-square bg-slate-800/40 rounded-2xl overflow-hidden border border-slate-700/50">
                <img :src="product.image" :alt="product.name" class="w-full h-full object-cover">
              </div>
              
              <!-- Additional Images -->
              <div class="grid grid-cols-4 gap-4">
                <div v-for="(image, index) in productImages" :key="index" 
                     @click="selectedImage = image"
                     class="aspect-square bg-slate-800/40 rounded-lg overflow-hidden border border-slate-700/50 cursor-pointer hover:border-emerald-500/50 transition-colors">
                  <img :src="image" :alt="product.name" class="w-full h-full object-cover">
                </div>
              </div>
            </div>

            <!-- Product Info -->
            <div class="space-y-8">
              <!-- Header -->
              <div>
                <div class="flex items-center space-x-2 mb-4">
                  <span class="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full border border-emerald-500/30">
                    {{ product.category }}
                  </span>
                  <span class="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full border border-blue-500/30">
                    SKU: {{ product.sku }}
                  </span>
                </div>
                
                <h1 class="text-3xl font-bold text-white mb-4">{{ product.name }}</h1>
                
                <div class="flex items-center space-x-4 mb-6">
                  <div class="flex items-center">
                    <div class="flex items-center">
                      <svg v-for="star in 5" :key="star" 
                           :class="star <= Math.floor(product.rating) ? 'text-yellow-400' : 'text-slate-600'"
                           class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    </div>
                    <span class="text-slate-400 ml-2">{{ product.rating }}/5</span>
                  </div>
                  <span class="text-slate-400">•</span>
                  <span class="text-slate-400">{{ product.stock }} in stock</span>
                </div>
              </div>

              <!-- Price -->
              <div class="bg-slate-800/40 rounded-2xl p-6 border border-slate-700/50">
                <div class="flex items-baseline space-x-2 mb-4">
                  <span class="text-4xl font-bold text-white">${{ product.price }}</span>
                  <span class="text-lg text-slate-400 line-through">${{ (product.price * 1.2).toFixed(2) }}</span>
                  <span class="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
                    Save 20%
                  </span>
                </div>
                
                <div class="space-y-4">
                  <div class="flex items-center space-x-4">
                    <div class="flex items-center space-x-2">
                      <button @click="quantity > 1 && quantity--" 
                              class="w-8 h-8 bg-slate-700/50 hover:bg-slate-600/50 rounded flex items-center justify-center text-white transition-colors">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                        </svg>
                      </button>
                      <span class="text-lg font-semibold text-white w-12 text-center">{{ quantity }}</span>
                      <button @click="quantity++" 
                              class="w-8 h-8 bg-slate-700/50 hover:bg-slate-600/50 rounded flex items-center justify-center text-white transition-colors">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                        </svg>
                      </button>
                    </div>
                    <span class="text-slate-400">Total: ${{ (product.price * quantity).toFixed(2) }}</span>
                  </div>
                  
                  <div class="flex space-x-3">
                    <button @click="addToCart" 
                            class="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center space-x-2">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
                      </svg>
                      <span>Add to Cart</span>
                    </button>
                    <button @click="buyNow" 
                            class="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors">
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div>
                <h3 class="text-lg font-semibold text-white mb-4">Description</h3>
                <p class="text-slate-300 leading-relaxed">{{ product.description }}</p>
              </div>

              <!-- Specifications -->
              <div>
                <h3 class="text-lg font-semibold text-white mb-4">Specifications</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-slate-800/40 rounded-lg p-4 border border-slate-700/50">
                    <div class="flex justify-between">
                      <span class="text-slate-400">Category</span>
                      <span class="text-white font-medium">{{ product.category }}</span>
                    </div>
                  </div>
                  <div class="bg-slate-800/40 rounded-lg p-4 border border-slate-700/50">
                    <div class="flex justify-between">
                      <span class="text-slate-400">SKU</span>
                      <span class="text-white font-medium">{{ product.sku }}</span>
                    </div>
                  </div>
                  <div class="bg-slate-800/40 rounded-lg p-4 border border-slate-700/50">
                    <div class="flex justify-between">
                      <span class="text-slate-400">Stock</span>
                      <span class="text-white font-medium">{{ product.stock }} units</span>
                    </div>
                  </div>
                  <div class="bg-slate-800/40 rounded-lg p-4 border border-slate-700/50">
                    <div class="flex justify-between">
                      <span class="text-slate-400">Rating</span>
                      <span class="text-white font-medium">{{ product.rating }}/5</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cart Sidebar -->
    <CartSidebar 
      :is-open="showCart" 
      @close="showCart = false"
      @order-complete="handleOrderComplete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CartSidebar from '../components/store/CartSidebar.vue'
import { storeService } from '../services/storeService'
import { useUserStore } from '../stores/userStore'

// Router and Store
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

// Reactive data
const loading = ref(true)
const showCart = ref(false)
const quantity = ref(1)
const selectedImage = ref('')

// Computed properties
const productId = computed(() => parseInt(route.params.id as string))
const product = computed(() => {
  const products = storeService.getAvailableProducts()
  return products.find(p => p.id === productId.value)
})

const productImages = computed(() => {
  if (!product.value) return []
  return [
    product.value.image,
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=400&fit=crop'
  ]
})

const userInitials = computed(() => {
  if (!userStore.currentUser) return 'U'
  const name = userStore.currentUser.name || ''
  const parts = name.split(' ')
  return parts.length >= 2 
    ? `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
    : name.charAt(0).toUpperCase()
})

const userName = computed(() => {
  return userStore.currentUser?.name || 'User'
})

const userTitle = computed(() => {
  return userStore.currentUser?.userType === 'contractor' ? 'Contractor' : 'User'
})

const cartItemCount = computed(() => storeService.getCartItemCount().value)

// Methods
const handleProfileClick = () => {
  router.push('/profile')
}

const logout = () => {
  userStore.logout()
  window.location.href = '/#/login'
}

const addToCart = () => {
  if (!product.value) return
  storeService.addToCart(product.value, 'product')
}

const buyNow = () => {
  if (!product.value) return
  storeService.addToCart(product.value, 'product')
  showCart.value = true
}

const handleOrderComplete = (order: any) => {
  console.log('Order completed:', order)
  router.push('/store?tab=orders')
}

// Lifecycle
onMounted(async () => {
  try {
    userStore.loadUserFromStorage()
    storeService.loadCartFromStorage()
    
    // Simulate loading
    await new Promise(resolve => setTimeout(resolve, 1000))
    loading.value = false
    
    if (product.value) {
      selectedImage.value = product.value.image
    }
  } catch (error) {
    console.error('Product view page error:', error)
    loading.value = false
  }
})
</script>

<style scoped>
/* Navigation Styles */
.nav-item {
  @apply flex items-center px-3 py-2 text-sm font-medium text-slate-300 rounded-lg transition-all duration-200 hover:text-white hover:bg-slate-700/50;
}

/* Animated Background */
.bg-grid-pattern {
  background-image: 
    linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
}

/* Floating Particles */
.particle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  animation: float 6s ease-in-out infinite;
}

.particle-1 {
  width: 4px;
  height: 4px;
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.particle-2 {
  width: 6px;
  height: 6px;
  top: 60%;
  left: 80%;
  animation-delay: 2s;
}

.particle-3 {
  width: 3px;
  height: 3px;
  top: 40%;
  left: 60%;
  animation-delay: 4s;
}

.particle-4 {
  width: 5px;
  height: 5px;
  top: 80%;
  left: 20%;
  animation-delay: 1s;
}

.particle-5 {
  width: 4px;
  height: 4px;
  top: 30%;
  left: 90%;
  animation-delay: 3s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-20px) translateX(10px);
    opacity: 1;
  }
}
</style>
